import logger from '@/services/logger';
import { httpService } from '../common/http.service';
import type { ApiResponse } from '../common/types/api-response';
import type { Exercise } from '../common/types/exercise';
import type { FinishWorkoutPayload } from './types/finish-workout-payload';
import type { Workout, WorkoutEvent } from './types/workout';
import type { StartWorkoutPayload } from './types/start-workout-payload';
import type { ScheduleWorkoutPayload } from './types/schedule-workout-payload';
import type { SubmitDifficultyFeedbackPayload } from './types/submit-difficulty-feedback-payload';
import type { Attributes } from '../common/types/attributes';
import type { FinishWorkoutResponse } from './types/finish-workout-response';
import type { CheckpointWorkoutPayload } from './types/checkpoint-workout-payload';
import { getMetaAppContext, trackMetaWorkoutCompleted } from '@/services/meta-app-events';
import type { EarlyExitWorkoutPayload } from './types/early-exit-workout-payload';

export async function getExercises() {
  try {
    const res = await httpService.get<{ exs: Exercise[] }>('exercises');
    return res.data.exs;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with getExercises',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}
export const getUserPrefExercises = async () => {
  try {
    const res = await httpService.get<ApiResponse<Exercise[]>>(`exercises/user-preferences`);
    return res.data.data || [];
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with getUserPrefExercises',
      data: {},
      error: err as Error,
    });
    throw err;
  }
};
export const getWorkoutEvents = async () => {
  try {
    const res = await httpService.get<ApiResponse<WorkoutEvent[]>>(`workout`);
    return res.data.data || [];
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with getWorkouts',
      data: {},
      error: err as Error,
    });
    throw err;
  }
};
export const getWorkoutEvent = async (workoutEventId: string) => {
  try {
    const res = await httpService.get<ApiResponse<WorkoutEvent>>(`workout/${workoutEventId}`);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with getWorkout',
      data: { workoutId: workoutEventId },
      error: err as Error,
    });
    throw err;
  }
};
export const getWorkoutFromPlanOrEventId = async (planOrWorkoutEventId: string) => {
  try {
    const res = await httpService.get<ApiResponse<Workout | WorkoutEvent>>(`workout/from-plan/${planOrWorkoutEventId}`);
    return res.data.data || null;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with getWorkoutFromPlanOrEventId',
      data: { planOrWorkoutEventId },
      error: err as Error,
    });
    throw err;
  }
};

export const startWorkout = async (payload: StartWorkoutPayload) => {
  try {
    const res = await httpService.post<ApiResponse<WorkoutEvent>>(`workout/start`, payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with startWorkout',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
};
export const scheduleWorkout = async (payload: ScheduleWorkoutPayload) => {
  try {
    const res = await httpService.post<ApiResponse<Workout>>(`workout/schedule`, payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with scheduleWorkout',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
};
export const finishWorkout = async (payload: FinishWorkoutPayload) => {
  try {
    const res = await httpService.post<ApiResponse<FinishWorkoutResponse>>(`workout/${payload.workoutEventId}/finish`, {
      ...payload,
      metaContext: await getMetaAppContext(),
    });
    void trackMetaWorkoutCompleted(res.data.data?.metaEventId);

    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with finishWorkout',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
};

export const checkpointWorkout = async (workoutEventId: string, payload: CheckpointWorkoutPayload) => {
  try {
    const res = await httpService.post<ApiResponse<unknown>>(`workout/${workoutEventId}/checkpoint`, payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with checkpointWorkout',
      data: { workoutEventId, payload },
      error: err as Error,
    });
    throw err;
  }
};

export const getRecoverableWorkout = async () => {
  try {
    const res = await httpService.get<ApiResponse<WorkoutEvent | null>>(`workout/recoverable`);
    return res.data.data ?? null;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with getRecoverableWorkout',
      data: {},
      error: err as Error,
    });
    throw err;
  }
};

export const finishWorkoutFromCheckpoint = async (workoutEventId: string) => {
  try {
    const res = await httpService.post<ApiResponse<FinishWorkoutResponse>>(
      `workout/${workoutEventId}/finish-from-checkpoint`,
      { metaContext: await getMetaAppContext() }
    );
    void trackMetaWorkoutCompleted(res.data.data?.metaEventId);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with finishWorkoutFromCheckpoint',
      data: { workoutEventId },
      error: err as Error,
    });
    throw err;
  }
};

export const earlyExitWorkout = async (payload: EarlyExitWorkoutPayload) => {
  try {
    const res = await httpService.post<ApiResponse<WorkoutEvent>>(
      `workout/${payload.workoutEventId}/early-exit`,
      payload
    );
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with earlyExitWorkout',
      // exitReasonText is free text typed by the user.
      data: { workoutEventId: payload.workoutEventId, exitReasons: payload.exitReasons },
      error: err as Error,
    });
    throw err;
  }
};

export const abandonWorkout = async (workoutEventId: string) => {
  try {
    const res = await httpService.post<ApiResponse<WorkoutEvent>>(`workout/${workoutEventId}/abandon`);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with abandonWorkout',
      data: { workoutEventId },
      error: err as Error,
    });
    throw err;
  }
};

export const submitWorkoutFeedback = async (payload: SubmitDifficultyFeedbackPayload) => {
  try {
    const res = await httpService.post<ApiResponse<Attributes>>(`workout/${payload.workoutEventId}/feedback`, payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[WorkoutAPI] - has issues with submitWorkoutFeedback',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
};
